import { ApexOptions } from 'apexcharts';
import { get } from 'lodash';
import { useCallback, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { renderToString } from 'react-dom/server';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { REGISTER_ID_SEARCH_PARAM_KEY } from '@/modules/common/constants/search-params.ts';
import BalancePieChart from '@/modules/issuer-dashboard/components/BalancePieChart.tsx';
import HolderSearch from '@/modules/issuer-dashboard/components/HolderSearch.tsx';
import { useIssuerRegisterClassListQuery } from '@/modules/issuer-dashboard/queries/registers.tsx';
import { useTopHoldersQuery } from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { issuerDashboardPaths } from '@/modules/issuer-dashboard/routes.tsx';
import { InfoCard, Select, Spinner } from 'ui';
import { goodFormatNumber, goodFormatUSDCurrency } from 'ui/utils';

interface TopHoldersProps {
  title?: string;
  subtitle?: boolean;
  holderSearch?: boolean;
}

interface TopHoldersChartItem {
  labels: string[];
  series: number[];
  percentages: number[];
}

export const TopHoldersChart = ({
  title = 'Holders',
  subtitle = false,
  holderSearch = false,
}: TopHoldersProps) => {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const containerId = useId();
  const legendTooltipId = `${containerId}-legend-tooltip`;
  const [holderById, setHolderById] = useState<number>();

  const currentRegisterId = +(params.get(REGISTER_ID_SEARCH_PARAM_KEY) || '0');
  const [registerClassFilter, setRegisterClassFilter] = useState<number | null>();

  const { data: dataTopHolders, isLoading: isTopHoldersLoading } = useTopHoldersQuery(
    { register_class_id: registerClassFilter || undefined },
    false,
  );

  const { data: classesData, isLoading: isLoadingClasses } = useIssuerRegisterClassListQuery(
    {},
    currentRegisterId,
  );

  const classesOptions = useMemo(
    () => [
      { label: 'All classes', value: 0 },
      ...(classesData?.registers || []).map(({ id, class_name }) => ({
        label: class_name,
        value: id,
      })),
    ],
    [classesData?.registers],
  );

  const { series, labels, percentages } = useMemo<TopHoldersChartItem>(
    () =>
      (dataTopHolders?.data?.data || []).reduce<TopHoldersChartItem>(
        ({ series, labels, percentages }, { balance, entity_name, percentage }) => ({
          series: [...series, balance],
          labels: [...labels, entity_name],
          percentages: [...percentages, percentage],
        }),
        { series: [], labels: [], percentages: [] },
      ),
    [dataTopHolders?.data?.data],
  );

  const addLegendTooltips = useCallback(
    (_: unknown, chartOptions: { config: ApexOptions }) => {
      const legendOptions = document.querySelectorAll(
        `#${CSS.escape(containerId)} .apexcharts-legend-series`,
      );

      const { series, labels } = chartOptions.config;

      if (!series || !labels) return;

      legendOptions.forEach(option => {
        const rel = option.getAttribute('rel');
        if (!rel || !+rel || typeof series?.[+rel - 1] === 'undefined') return;
        const balance = series[+rel - 1];
        const name = labels[+rel - 1];

        option.setAttribute('data-tooltip-id', legendTooltipId);
        option.setAttribute(
          'data-tooltip-content',
          `${name}: ${goodFormatUSDCurrency(balance as number)}`,
        );
      });
    },
    [containerId, legendTooltipId],
  );

  const handleLegendClick = useCallback((entityId: number) => {
    setHolderById(entityId);
  }, []);

  const options = useMemo<ApexOptions>(
    () => ({
      chart: {
        events: {
          mounted: addLegendTooltips,
          updated: addLegendTooltips,
          legendClick: (_, index) => {
            const entityId = get(dataTopHolders?.data, `data.${index}.entity_id`);
            if (entityId) {
              handleLegendClick(entityId);
            }
          },
        },
      },
      responsive: [
        {
          breakpoint: 768,
          options: { plotOptions: { pie: { offsetY: 0 } } },
        },
      ],
      legend: {
        offsetY: -20,
        offsetX: -50,
        formatter: (label: string, { seriesIndex }) => {
          return renderToString(
            <div className="min-w-0 w-full flex items-center justify-between gap-2">
              <div className="min-w-0 flex-1 truncate">{label}</div>
              <div className="shrink-0 font-semibold">
                {goodFormatNumber(percentages[seriesIndex])}%
              </div>
            </div>,
          );
        },
      },
      plotOptions: { pie: { offsetY: 50 } },
      tooltip: {
        custom: ({ seriesIndex, series, w }) =>
          renderToString(
            <div
              className="flex flex-col gap-1 whitespace-normal p-2"
              style={{ maxWidth: 'min(320px, calc(100vw - 32px))', overflowWrap: 'anywhere' }}
            >
              <div>{w.config.labels[seriesIndex]}</div>
              <div>{goodFormatUSDCurrency(series[seriesIndex])}</div>
            </div>,
          ),
      },
    }),
    [addLegendTooltips, dataTopHolders?.data, handleLegendClick, percentages],
  );

  if (holderById) {
    return (
      <Navigate
        to={{
          pathname: issuerDashboardPaths.dynamic.holder(holderById.toString()),
          search: params.toString(),
        }}
        state={{ from: pathname + '?' + params.toString() }}
      />
    );
  }

  return (
    <InfoCard title={title}>
      {typeof document !== 'undefined' &&
        createPortal(
          <ReactTooltip
            id={legendTooltipId}
            positionStrategy="fixed"
            place="top"
            offset={8}
            variant="light"
            className="border shadow-md"
            style={{
              zIndex: 50,
              maxWidth: 'min(360px, calc(100vw - 32px))',
              whiteSpace: 'normal',
              overflowWrap: 'anywhere',
            }}
          />,
          document.body,
        )}
      <div className="min-w-0 flex flex-col" id={containerId}>
        {holderSearch && (
          <>
            <p className="text-sm font-semibold mb-4">Find holder by name or ID</p>
            <div className="mb-6">
              <HolderSearch />
            </div>
          </>
        )}

        {subtitle && <span className="text-sm font-semibold mb-3 md:mb-0">Top holders</span>}
        <div className="relative">
          <Select
            isClearable={!!registerClassFilter}
            label=""
            placeholder="All classes"
            classNameLabel="!mb-0"
            className="md:absolute w-full md:w-1/2 md:top-[7px] h-10 z-[2]"
            onChange={setRegisterClassFilter}
            value={registerClassFilter || 0}
            options={classesOptions}
            withBorder
          />
          {(isLoadingClasses || isTopHoldersLoading) && (
            <div className="p-4 w-full h-[440.69px] xl:h-[267px] flex items-center justify-center">
              <Spinner size="lg" />
            </div>
          )}
          {!isLoadingClasses && !isTopHoldersLoading && (
            <BalancePieChart options={options} series={series} labels={labels} />
          )}
        </div>
      </div>
    </InfoCard>
  );
};
